import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme, CssBaseline } from '@mui/material';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Layout from './components/Layout';
import Login from './pages/Login';
import OAuthCallback from './pages/OAuthCallback';
import Users from './pages/Users';
import Companies from './pages/Companies';
import Customers from './pages/Customers';
import BIDashboard from './pages/BIDashboard';
import DailyTonerDispatchReport from './pages/DailyTonerDispatchReport';
import FinancialAnalytics from './pages/FinancialAnalytics';
import SalesCatalog from './pages/SalesCatalog';
import ClientMerge from './pages/ClientMerge';
import MFPModelMerge from './pages/MFPModelMerge';
import SupplyKPIs from './pages/SupplyKPIs';
import ClientProfitability from './pages/ClientProfitability';
import ProfitabilityChat from './pages/ProfitabilityChat';
import Equipment from './pages/Equipment';
import InventoryAdjustments from './pages/InventoryAdjustments';
import CashFlow from './pages/CashFlow';
import FleetClients from './pages/FleetClients';
import FleetAgents from './pages/FleetAgents';
import FleetADSync from './pages/FleetADSync';
import FleetAnalytics from './pages/FleetAnalytics';
import FleetAlertRules from './pages/FleetAlertRules';
import SalesPipeline from './pages/SalesPipeline';
import EffectiveCoverage from './pages/EffectiveCoverage';
import OnPremClients from './pages/OnPremClients';

const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
  },
});

const PrivateRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return <div>Cargando...</div>;
  }

  return user ? <Layout>{children}</Layout> : <Navigate to="/login" />;
};

const RootRedirect: React.FC = () => {
  const { isLoading } = useAuth();

  if (isLoading) {
    return <div>Cargando...</div>;
  }

  // Always redirect to login first; if user is authenticated, Login component will redirect
  return <Navigate to="/login" replace />;
};

const App: React.FC = () => {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <Router>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/auth/callback" element={<OAuthCallback />} />
            <Route
              path="/users"
              element={
                <PrivateRoute>
                  <Users />
                </PrivateRoute>
              }
            />
            <Route
              path="/companies"
              element={
                <PrivateRoute>
                  <Companies />
                </PrivateRoute>
              }
            />
            <Route
              path="/customers"
              element={
                <PrivateRoute>
                  <Customers />
                </PrivateRoute>
              }
            />
            <Route
              path="/bi-dashboard"
              element={
                <PrivateRoute>
                  <BIDashboard />
                </PrivateRoute>
              }
            />
            <Route
              path="/toner-dispatch-report"
              element={
                <PrivateRoute>
                  <DailyTonerDispatchReport />
                </PrivateRoute>
              }
            />
            <Route
              path="/financial-analytics"
              element={
                <PrivateRoute>
                  <FinancialAnalytics />
                </PrivateRoute>
              }
            />
            <Route
              path="/sales-catalog"
              element={
                <PrivateRoute>
                  <SalesCatalog />
                </PrivateRoute>
              }
            />
            <Route
              path="/client-merge"
              element={
                <PrivateRoute>
                  <ClientMerge />
                </PrivateRoute>
              }
            />
            <Route
              path="/model-merge"
              element={
                <PrivateRoute>
                  <MFPModelMerge />
                </PrivateRoute>
              }
            />
            <Route
              path="/supply-kpis"
              element={
                <PrivateRoute>
                  <SupplyKPIs />
                </PrivateRoute>
              }
            />
            <Route
              path="/client-profitability"
              element={
                <PrivateRoute>
                  <ClientProfitability />
                </PrivateRoute>
              }
            />
            <Route
              path="/equipment"
              element={
                <PrivateRoute>
                  <Equipment />
                </PrivateRoute>
              }
            />
            <Route
              path="/inventory-adjustments"
              element={
                <PrivateRoute>
                  <InventoryAdjustments />
                </PrivateRoute>
              }
            />
            <Route
              path="/profitability-chat"
              element={
                <PrivateRoute>
                  <ProfitabilityChat />
                </PrivateRoute>
              }
            />
            <Route
              path="/cash-flow"
              element={
                <PrivateRoute>
                  <CashFlow />
                </PrivateRoute>
              }
            />
            <Route
              path="/fleet-clients"
              element={
                <PrivateRoute>
                  <FleetClients />
                </PrivateRoute>
              }
            />
            <Route
              path="/fleet-agents"
              element={
                <PrivateRoute>
                  <FleetAgents />
                </PrivateRoute>
              }
            />
            <Route
              path="/fleet-ad-sync"
              element={
                <PrivateRoute>
                  <FleetADSync />
                </PrivateRoute>
              }
            />
            <Route
              path="/fleet-analytics"
              element={
                <PrivateRoute>
                  <FleetAnalytics />
                </PrivateRoute>
              }
            />
            <Route
              path="/fleet-alerts"
              element={
                <PrivateRoute>
                  <FleetAlertRules />
                </PrivateRoute>
              }
            />
            <Route
              path="/sales-pipeline"
              element={
                <PrivateRoute>
                  <SalesPipeline />
                </PrivateRoute>
              }
            />
            <Route
              path="/effective-coverage"
              element={
                <PrivateRoute>
                  <EffectiveCoverage />
                </PrivateRoute>
              }
            />
            <Route
              path="/onprem-clients"
              element={
                <PrivateRoute>
                  <OnPremClients />
                </PrivateRoute>
              }
            />
            <Route path="/" element={<RootRedirect />} />
          </Routes>
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
};

export default App;
